import React, { useEffect, useRef, useState } from "react";
import { MagnifyingGlass, X, CaretDown, CaretUp, ShareNetwork, Check } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * CaptionSearchBox — "🔎 Search inside this sermon" UI.
 *
 * Drops anywhere a media player lives (sermons, movies, devotionals).
 * Calls `GET /api/captions/search?url=<media_url>&q=<phrase>` and
 * renders the timestamped matches. Tapping a hit calls `onSeek(start)`
 * so the parent player can jump to that exact second.
 *
 * UX notes:
 *   • Starts collapsed as a 32px chip — doesn't crowd the player UI.
 *   • Auto-expands when the user taps it.
 *   • Debounces input by 250ms so typing doesn't hammer the backend.
 *   • Tells the user when captions aren't ready yet — captions are
 *     generated on first play, so the user needs to let the audio
 *     play through at least once before search will return hits.
 *   • Renders match snippets with <mark> highlights from the backend.
 *     The backend escapes HTML before wrapping, so dangerouslySet is
 *     safe here.
 */
export default function CaptionSearchBox({ mediaUrl, onSeek, accent = "terracotta", shareItem = null }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Auto-focus the input when the box expands. Without this, mobile
  // users have to tap twice — once to expand, once to type.
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  // Debounced search. We cancel the in-flight timer on every keystroke
  // so the user can type "John 3:16" and only fire one request after
  // they stop typing.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim() || !mediaUrl) {
      setHits([]);
      setError(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get("/captions/search", {
          params: { url: mediaUrl, q: q.trim(), limit: 50 },
        });
        setHits(res.data.hits || []);
      } catch (e) {
        const detail = e?.response?.data?.detail;
        // 404 means "no captions yet". Surface a useful hint instead
        // of a generic error.
        if (e?.response?.status === 404) {
          setError("Captions haven't been generated for this sermon yet. Play the audio for a few seconds, then try again.");
        } else if (typeof detail === "string") {
          setError(detail);
        } else {
          setError("Search failed — try again in a moment.");
        }
        setHits([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [q, mediaUrl]);

  if (!mediaUrl) return null;

  // Collapsed pill — text-only, no pill background. User asked to
  // keep the words but remove the colored pill so the chrome stays
  // visually quiet over a dark video frame.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="caption-search-open"
        data-no-drag="1"
        title="Search inside this sermon — jump to the exact moment a phrase was spoken"
        className="inline-flex items-center gap-1.5 px-1 h-9 text-cream/85 hover:text-cream text-[11px] tracking-[0.18em] uppercase font-bold transition-all"
      >
        <MagnifyingGlass size={14} weight="bold" />
        <span>Search in this</span>
      </button>
    );
  }

  return (
    <div
      data-testid="caption-search-box"
      data-no-drag="1"
      className="rounded-2xl bg-ink/85 backdrop-blur-md text-cream border border-cream/15 shadow-2xl w-[min(92vw,420px)] max-h-[60vh] overflow-hidden flex flex-col"
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-cream/10">
        <MagnifyingGlass size={14} weight="bold" className="opacity-80" />
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search this sermon's transcript…"
          data-testid="caption-search-input"
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-cream/40"
        />
        <button
          type="button"
          onClick={() => { setOpen(false); setQ(""); setHits([]); }}
          aria-label="Close search"
          data-testid="caption-search-close"
          className="w-7 h-7 grid place-items-center rounded-full hover:bg-cream/15"
        >
          <X size={12} weight="bold" />
        </button>
      </div>

      <div className="overflow-y-auto flex-1">
        {loading && (
          <div className="px-4 py-6 text-center text-xs opacity-70">Searching transcript…</div>
        )}
        {!loading && error && (
          <div className="px-4 py-5 text-xs leading-snug" data-testid="caption-search-error">
            {error}
          </div>
        )}
        {!loading && !error && q.trim() && hits.length === 0 && (
          <div className="px-4 py-5 text-xs opacity-70" data-testid="caption-search-empty">
            No matches found for "{q.trim()}".
          </div>
        )}
        {!loading && !error && !q.trim() && (
          <div className="px-4 py-5 text-xs opacity-70">
            Type a phrase to find every spot in this sermon where it's spoken — tap a result to jump straight there.
          </div>
        )}
        {hits.map((h, idx) => (
          <div
            key={`${h.start}-${idx}`}
            className="w-full text-left border-t border-cream/5 hover:bg-cream/5 transition-all flex items-stretch"
          >
            <button
              type="button"
              data-testid={`caption-hit-${idx}`}
              onClick={() => {
                try { onSeek?.(h.start); } catch (_) {}
              }}
              className="flex-1 text-left px-4 py-3"
            >
              <div className={`text-[10px] tracking-[0.22em] uppercase font-bold text-${accent}/90 mb-1 tabular-nums`}>
                {formatTimestamp(h.start)}
              </div>
              <div
                className="text-sm leading-snug"
                // Backend escaped HTML before wrapping the match in <mark>,
                // so this is safe — the only HTML in the snippet comes from
                // our own server.
                dangerouslySetInnerHTML={{ __html: h.snippet }}
              />
            </button>
            {shareItem?.id && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  shareMoment(
                    { ...shareItem, mediaUrl },
                    h.start,
                    h.text || h.snippet
                  );
                }}
                data-testid={`caption-share-${idx}`}
                data-no-drag="1"
                title="Copy a link to this exact moment — paste it into a text or post and your friend will land right here"
                aria-label="Share this exact moment"
                className="shrink-0 w-12 grid place-items-center border-l border-cream/5 hover:bg-cream/10"
              >
                <ShareNetwork size={14} weight="bold" className="opacity-80" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// "1:23:45" / "12:34" / "0:42" — drops the hour part when the cue is
// less than an hour in, so short songs/devotionals don't read "0:00:42".
function formatTimestamp(totalSec) {
  const t = Math.max(0, Math.floor(totalSec || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n) => String(n).padStart(2, "0");
  if (h > 0) return `${h}:${pad(m)}:${pad(s)}`;
  return `${m}:${pad(s)}`;
}

// Build a "share this exact moment" URL and copy it to the clipboard.
// Falls back to Web Share API on mobile so the user gets a native share
// sheet (text, Whatsapp, IG, etc).  URL format:
//   /<current-section>?play=<item.id>&t=<seconds>
// MediaGrid reads `?play` + `?t` on mount and opens the player straight
// to that moment. We ALSO post the share event to /api/discover/share
// so it can rank on the Discover-this-week leaderboard.
async function shareMoment(item, startSec, snippet) {
  if (typeof window === "undefined") return;
  const t = Math.max(0, Math.floor(startSec || 0));
  // Strip any existing query string from the path so we don't double-
  // up filters / search params from the page the user is on.
  const path = window.location.pathname || "/";
  const url = `${window.location.origin}${path}?play=${encodeURIComponent(item.id)}&t=${t}`;
  const title = item.title || "A moment worth hearing";
  const text = `Listen to this moment in “${title}” on Holy Words.`;

  // Log to the discover leaderboard before opening the share sheet so
  // that even if the user bails out of sharing we still count their
  // intent. Fire-and-forget — failures here are never user-visible.
  try {
    await api.post("/discover/share", {
      item_id: item.id,
      item_title: title,
      media_url: item.mediaUrl || "",
      start_sec: t,
      section: (path.split("/").filter(Boolean)[0] || "sermons").toLowerCase(),
      snippet: (snippet || "").replace(/<[^>]+>/g, "").slice(0, 400),
    });
  } catch (_) { /* don't block the share sheet on a telemetry failure */ }

  // Web Share is gold on iOS/Android — drops the user into the native
  // share sheet. Fall back to clipboard everywhere else.
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return;
    } catch (e) {
      // User canceled the share sheet — no toast needed.
      if (e && e.name === "AbortError") return;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success("Moment link copied", { description: url });
  } catch (_) {
    // Final fallback — just show the URL in the toast so the user can
    // long-press to copy manually.
    toast.message("Share this link", { description: url });
  }
}
